import type { InventoryUnit } from '../data/inventory';

/**
 * Parses asset tag strings like USA-TIME-1234567890 (Code 128):
 * market prefix — product type — serial (last segment).
 * Older tags start with US, or with the country of origin (China-TIME-1234567890).
 * The parser still splits those the same way so a scan of an old label works.
 */

/** Every composed serial starts with this. Country of origin is stored separately. */
export const ASSET_TAG_MARKET = 'USA';

/** Prefix used before the tag was changed to USA. */
const LEGACY_ASSET_TAG_MARKET = 'US';

export type ParsedAssetTag = {
  countryOfOrigin: string;
  productType: string;
  serialNumber: string;
};

export function parseAssetTagBarcode(raw: string): ParsedAssetTag | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const parts = trimmed.split('-').map((p) => p.trim()).filter((p) => p.length > 0);
  if (parts.length >= 3) {
    const countryOfOrigin = parts[0];
    const serialNumber = parts[parts.length - 1];
    const productType = parts.slice(1, -1).join('-');
    if (!countryOfOrigin || !productType || !serialNumber) return null;
    return { countryOfOrigin, productType, serialNumber };
  }
  /** e.g. USA-Garg 1234567890 (space before serial, two hyphen segments) */
  if (parts.length === 2) {
    const countryOfOrigin = parts[0];
    const tail = parts[1];
    const spaceMatch = tail.match(/^(.+?)\s+(\S.+)$/);
    if (spaceMatch) {
      const productType = spaceMatch[1].trim();
      const serialNumber = spaceMatch[2].trim();
      if (countryOfOrigin && productType && serialNumber) {
        return { countryOfOrigin, productType, serialNumber };
      }
    }
  }
  return null;
}

function norm(s: string | undefined): string {
  return (s ?? '').trim().toLowerCase();
}

/**
 * True when `serial` begins with `prefix` (case-insensitive). `prefix` includes
 * its trailing hyphen.
 */
function startsWithPrefix(serial: string, prefix: string): boolean {
  return prefix.length > 0 && serial.toLowerCase().startsWith(prefix.toLowerCase());
}

/**
 * The doubled TIM-E tag: a country, the product type, then US or USA and the
 * product type again (CHINA-TIME-US-TIME-123456818). Returns the text after
 * that second prefix, or null when the serial is not in that shape. The leading
 * country token does not have to match the country column.
 */
function stripDoubledMarketPrefix(serial: string, productType: string): string | null {
  for (const market of [ASSET_TAG_MARKET, LEGACY_ASSET_TAG_MARKET]) {
    const marker = `-${productType}-${market}-${productType}-`.toLowerCase();
    const pos = serial.toLowerCase().indexOf(marker);
    if (pos <= 0) continue;
    const head = serial.slice(0, pos);
    if (head.includes('-')) continue;
    return serial.slice(pos + marker.length);
  }
  return null;
}

/**
 * Builds the asset-tag serial as USA-PRODUCTTYPE-NUMBER. Country of origin is not
 * part of the tag. `country` is only used to peel an older country-type prefix
 * off a value that was already composed, including the doubled
 * CHINA-TIME-US-TIME-number form, so saving again does not stack another prefix.
 */
export function composeAssetTagSerial(
  country: string | undefined,
  productType: string | undefined,
  serial: string | undefined
): string {
  const pt = (productType ?? '').trim();
  const portion = extractSerialNumberPortion(serial, country, pt);
  if (!pt) return portion;
  if (!portion) return '';
  return [ASSET_TAG_MARKET, pt, portion].join('-');
}

/**
 * Inverse of {@link composeAssetTagSerial}: the editable number portion.
 * Strips USA-type-, the older US-type-, the unit's country-type-, and a doubled
 * country-type-US-type- prefix. A value that isn't prefixed is returned unchanged.
 */
export function extractSerialNumberPortion(
  fullSerial: string | undefined,
  country: string | undefined,
  productType: string | undefined
): string {
  let s = (fullSerial ?? '').trim();
  const pt = (productType ?? '').trim();
  const co = (country ?? '').trim();
  if (!s || !pt) return s;
  for (let i = 0; i < 6; i++) {
    const marketPrefix = [ASSET_TAG_MARKET, LEGACY_ASSET_TAG_MARKET]
      .map((market) => `${market}-${pt}-`)
      .find((prefix) => startsWithPrefix(s, prefix));
    if (marketPrefix) {
      s = s.slice(marketPrefix.length);
      continue;
    }
    if (co) {
      const countryPrefix = `${co}-${pt}-`;
      if (startsWithPrefix(s, countryPrefix)) {
        s = s.slice(countryPrefix.length);
        continue;
      }
    }
    const doubled = stripDoubledMarketPrefix(s, pt);
    if (doubled != null && doubled !== s) {
      s = doubled;
      continue;
    }
    break;
  }
  return s.trim();
}

/**
 * Returns an existing unit whose serial number exactly matches the given value
 * (case-insensitive, trimmed). Used to block duplicate entry regardless of how the
 * serial was produced (scan, manual entry, or composed USA-TYPE-SERIAL).
 */
export function findUnitBySerialNumber(
  units: InventoryUnit[],
  serial: string | undefined
): InventoryUnit | undefined {
  const target = norm(serial);
  if (!target) return undefined;
  return units.find((u) => norm(u.serialNumber) === target);
}

/**
 * Returns an existing unit whose country, serial, and product type (asset tag category)
 * match the parsed scan. Product type must match when both sides have a value.
 */
export function findUnitMatchingParsedBarcode(
  units: InventoryUnit[],
  parsed: ParsedAssetTag
): InventoryUnit | undefined {
  const ps = norm(parsed.serialNumber);
  const pc = norm(parsed.countryOfOrigin);
  const pt = norm(parsed.productType);
  /** Current tag: USA-type-number, whatever country the label used to start with. */
  const canonical = norm(composeAssetTagSerial(parsed.countryOfOrigin, parsed.productType, parsed.serialNumber));
  /** Older tag: country-type-number, or US-type-number from before the USA prefix. */
  const legacy = norm(
    [parsed.countryOfOrigin, parsed.productType, parsed.serialNumber].filter(Boolean).join('-')
  );
  if (!ps) return undefined;

  for (const u of units) {
    const us = norm(u.serialNumber);
    const matchesCanonical = us === canonical;
    if (!matchesCanonical && us !== ps && us !== legacy) continue;
    // The USA prefix is not the country of origin, so a canonical match does not
    // require the barcode's first segment to equal the unit's country.
    if (!matchesCanonical && norm(u.countryOfOrigin) !== pc) continue;
    const upt = norm(u.productType);
    if (pt && upt && pt !== upt) continue;
    return u;
  }
  return undefined;
}
